// What the verification view says, in words (TGCLOUD-1868, plan D3, D4, D5 and D6). Pure: no React.
//
// THE CODES ARE THE BACKEND'S. Every list below is a copy of backend/services/testResults/rules.js,
// and labReportVerificationVocabulary.test.js reads rules.js and fails when they part: a reason the
// server gives that the view cannot name would put a code on screen, and a word for a code the
// server never sends is a sentence nobody will ever read.
//
// HOUSE WORDING. Plain, sentence case, and never a code. A reason is said as the end of a sentence
// ("No source location: the report does not print it"), so each one starts in lower case.
//
// IN THE SELECTED LANGUAGE (TGCLOUD-1893). The constants stay English, the keys t() looks up, and
// are translated where they are shown: `t(SHOW_IN_REPORT)`. Each helper that says a sentence takes
// the caller's `t` as its last argument, defaulting to English, and says the sentence as ONE key
// with its numbers and values as placeholders, filled in after the lookup.
import { english } from 'src/i18n/language';

const freeze = (values) => Object.freeze([...values]);

// ── Where a value is in the report (plan A2, D3) ───────────────────────────────────────────────

// rules.UNANCHORED_REASONS: why a value read from a report has no place in it to point at.
export const UNANCHORED_REASONS = freeze([
  'not-printed',
  'derived',
  'ai-first-occurrence',
  'alignment-failed',
  'no-text-layer',
  'parse-failed'
]);

export const UNANCHORED_REASON_TEXT = Object.freeze({
  'not-printed': 'the report does not print it',
  derived: 'it is worked out, not printed in one place',
  'ai-first-occurrence':
    'found by AI, so its place in the report is not certain',
  'alignment-failed': 'the report’s text could not be matched to its pages',
  'no-text-layer': 'the report has no text to point to',
  'parse-failed': 'the report could not be read as text'
});

// A value read from the report whose place the answer does not give and does not explain: a span
// the boxes could not be worked out for. Said, never guessed at.
export const UNKNOWN_PLACE_TEXT = 'its place in the report is not known';

/** A reason in words: one of UNANCHORED_REASON_TEXT, else UNKNOWN_PLACE_TEXT. */
export const unanchoredReasonText = (reason, t = english) =>
  t(
    (typeof reason === 'string' &&
      Object.prototype.hasOwnProperty.call(UNANCHORED_REASON_TEXT, reason) &&
      UNANCHORED_REASON_TEXT[reason]) ||
      UNKNOWN_PLACE_TEXT
  );

// The anchor mark beside a value (AnchorMark.jsx).
export const SHOW_IN_REPORT = 'Show in the report';
export const NO_SOURCE_LOCATION = 'No source location';
export const UNVERIFIABLE_MARK = 'Cannot be checked here';
export const UNVERIFIABLE_REASON = 'the report could not be shown';

/** "No source location: the report does not print it": `reasonText` in words, already translated. */
export const noSourceLocationText = (reasonText, t = english) =>
  t('No source location: {{reason}}', { reason: reasonText });

/** "Cannot be checked here: the report could not be shown" (D5). */
export const unverifiableText = (t = english) =>
  t('Cannot be checked here: {{reason}}', { reason: t(UNVERIFIABLE_REASON) });

// ── A report that cannot be shown (plan D5) ────────────────────────────────────────────────────

export const REPORT_NOT_SHOWN =
  'The report could not be shown here, so these values cannot be checked against it.';
// Why Save waits while the report is still on its way: a report never drawn was never checked
// against. The pane gives up after a while, and then asks for the acknowledgement below.
export const WAIT_FOR_REPORT =
  'Wait for the report to be shown beside the Test.';
// The footer's checkbox ([view-groups]), required to save while the report cannot be shown; it
// sends labReportApplication.acknowledgeUnverifiable.
export const UNVERIFIABLE_ACKNOWLEDGEMENT =
  'I have checked these values against the report another way';

// ── How a saved report was checked (plan B5, D6) ───────────────────────────────────────────────

// rules.VERIFICATION_GROUP_KINDS: the groups a clinician confirms.
export const VERIFICATION_GROUP_KINDS = freeze([
  'gene-list',
  'variant-identity',
  'variant-classification',
  'overall-result'
]);

// A group's name. A variant's groups name the variant after these words, where the Test still
// holds it.
export const GROUP_KIND_TEXT = Object.freeze({
  'gene-list': 'Gene list',
  'variant-identity': 'Variant',
  'variant-classification': 'Laboratory classification',
  'overall-result': 'Overall result'
});

// rules.VERIFICATION_OUTCOMES: what the save found a group to be.
export const VERIFICATION_OUTCOMES = freeze([
  'as-read',
  'edited',
  'typed',
  'manual',
  'none-captured'
]);

export const OUTCOME_TEXT = Object.freeze({
  'as-read': 'confirmed as read from the report',
  edited: 'confirmed after a value read from the report was changed',
  typed:
    'confirmed with values entered by hand where the reading was not certain',
  manual: 'entered by hand: the report’s gene list was not offered',
  'none-captured': 'nothing of it was saved'
});

/** An outcome in words, or a plain fallback for one this list does not know. */
export const outcomeText = (outcome, t = english) =>
  (typeof outcome === 'string' &&
    Object.prototype.hasOwnProperty.call(OUTCOME_TEXT, outcome) &&
    t(OUTCOME_TEXT[outcome])) ||
  t('confirmed');

// A value typed into a group, against what was read (provenance.groups[].typed[].matchedReading).
export const TYPED_MATCH_TEXT = Object.freeze({
  true: 'matched what was read',
  false: 'differed from what was read',
  null: 'nothing was read to compare it with'
});

/** "2 matched what was read": how many typed values compared each way (TYPED_MATCH_TEXT's keys). */
export const typedMatchCountText = (key, count, t = english) =>
  key === 'true'
    ? t('{{count}} matched what was read', { count })
    : key === 'false'
    ? t('{{count}} differed from what was read', { count })
    : t('{{count}} nothing was read to compare it with', { count });

export const NO_CONFIRMATIONS =
  'No confirmations are recorded: this report was applied before each part was confirmed on its own.';

// ── Confirming a reading, part by part (plan D3, D4; decisions 13, 14, 15) ──────────────────────

// rules.LOW_CONFIDENCE_REASONS: why a value of a group arrived empty. Each is the end of a sentence
// ("Not filled in: the report does not state it"), so each starts in lower case.
export const LOW_CONFIDENCE_REASONS = freeze([
  'parse-failed',
  'alignment-failed',
  'ai-read',
  'not-stated',
  'other-scale',
  'incomplete',
  'contradicts',
  'classification-choices',
  'ambiguous-date',
  'sentence-not-row',
  'row-incomplete',
  'heuristic-rule',
  'unanchored',
  'gene-list-gate'
]);

export const LOW_CONFIDENCE_REASON_TEXT = Object.freeze({
  'parse-failed': 'the report could not be read as text',
  'alignment-failed': 'the report’s text could not be matched to its pages',
  'ai-read': 'it was found by AI, not read by the rules',
  'not-stated': 'the report does not state it',
  // A somatic tier ("Tier IA"): the lab's words, kept as printed, name none of the five classes.
  'other-scale': 'the report classifies it on another scale',
  incomplete: 'the reading could not complete it',
  contradicts: 'its row in the report also says something against it',
  'classification-choices':
    'its row in the report names more than one classification',
  'ambiguous-date': 'the date can be read two ways',
  'sentence-not-row': 'it was read from a sentence, not from a results table',
  'row-incomplete': 'its row in the results table is missing a cell',
  'heuristic-rule': 'it was read without a label in the report to go by',
  unanchored: 'it has no place in the report to show',
  'gene-list-gate': 'the report’s gene list could not be taken as printed'
});

/** Why a value arrived empty, in words; a plain fallback for a reason this list does not know. */
export const lowConfidenceReasonText = (reason, t = english) =>
  (typeof reason === 'string' &&
    Object.prototype.hasOwnProperty.call(LOW_CONFIDENCE_REASON_TEXT, reason) &&
    t(LOW_CONFIDENCE_REASON_TEXT[reason])) ||
  t('it could not be read with confidence');

// rules.GENE_LIST_GATES: whether the report's gene list could be taken as the genes analysed.
export const GENE_LIST_GATES = freeze([
  'ok',
  'mismatch',
  'ambiguous',
  'inferred',
  'none-read',
  'rows-unread'
]);

/**
 * Why a report's gene list was withheld, in a sentence: the stated count and the count read where
 * they differ.
 */
export function geneListGateText(geneList, t = english) {
  const gate = geneList && geneList.gate;
  const stated =
    geneList && geneList.stated && Number.isFinite(geneList.stated.count)
      ? geneList.stated.count
      : null;
  const read =
    geneList && Number.isFinite(geneList.readCount) ? geneList.readCount : 0;
  switch (gate) {
    case 'ok':
      return '';
    case 'mismatch': {
      if (stated === null) {
        return t(
          'The number of genes the report states differs from the number read.'
        );
      }
      const vars = { stated, read };
      if (stated === 1) {
        return read === 1
          ? t('The report states {{stated}} gene, and {{read}} was read.', vars)
          : t(
              'The report states {{stated}} gene, and {{read}} were read.',
              vars
            );
      }
      return read === 1
        ? t('The report states {{stated}} genes, and {{read}} was read.', vars)
        : t(
            'The report states {{stated}} genes, and {{read}} were read.',
            vars
          );
    }
    case 'ambiguous':
      return t('The report states more than one number of genes.');
    case 'inferred':
      return t(
        'The genes were read from the results, not from a printed list of the genes analysed.'
      );
    case 'none-read':
      return t('No list of the genes analysed was read from the report.');
    case 'rows-unread':
      return t(
        'Not everything this person’s rows of the results table print was read, so the report’s gene list is not taken as theirs.'
      );
    default:
      return t('The report’s gene list could not be taken as printed.');
  }
}

// The words of the group cards, the low-confidence fields and the footer.
export const CONFIRMED = 'Confirmed';
export const TYPE_AS_PRINTED = 'Type it as printed';
export const MATCHES_READING = 'Matches what was read';
export const DIFFERS_FROM_READING =
  'Differs from what was read. Check the report.';
export const NOT_FILLED_IN = 'Not filled in';
// The choice beside an interpretation's five tiers where the reading holds none of them: saved as
// no interpretation (verificationGroups.NOT_STATED_FIELDS), never as a tier the lab did not give.
export const NOT_STATED_LABEL = 'Not stated';
export const CONCLUSION_NOT_READ =
  'The report’s conclusion could not be read as a result. Read it (highlighted) and choose the Test result.';
// The same, where the conclusion has no place in the report to highlight (or the report cannot be
// shown): "(highlighted)" would promise a highlight there is not.
export const CONCLUSION_NOT_READ_UNPLACED =
  'The report’s conclusion could not be read as a result. Read it in the report and choose the Test result.';
// Decision 15: the lab's wording of a classification is saved verbatim, whatever is chosen beside it.
export const LAB_WORDING_KEPT =
  'Saved with the laboratory’s own wording, as printed.';
export const ENTER_GENES_BY_HAND =
  'Enter the genes the report lists as analysed';
// The count of genes the report states it analysed, as its place in the report is named.
export const STATED_COUNT_LABEL = 'The number of genes the report states';
/** "Lab report number, as printed: NB26-PN-00416": a reference of the report, at the Test's head. */
export const reportKeyText = (label, value, t = english) =>
  t('{{label}}, as printed: {{value}}', { label, value });
export const WITHHELD_GENES_LEAD =
  'The report names these genes. They are not added for you: enter the ones it lists as analysed.';
export const LIST_STATE_QUESTION = 'What is this list of genes?';
// Decision 13: no list of an exome or a genome is every gene the laboratory analysed.
export const COMPLETE_NOT_OFFERED_FOR_SCOPE =
  'An exome or a genome is recorded by its scope, so its gene list is never every gene the laboratory analysed.';
export const NOTHING_SAVED_YET = 'Nothing is saved until you save the Test.';
export const NOT_READ_HEADING = 'Not read from this report';

/**
 * "Confirm the gene list (22 genes, as stated)", or with no count stated, "(3 genes)". It names the
 * rows the Test holds, which is what is confirmed: where they are not the number the report
 * states, it says both ("(6 genes; the report states 5)"), never "as stated" (review of the view).
 */
export function confirmGeneListLabel(
  { statedCount = null, rowCount = 0 },
  t = english
) {
  if (Number.isFinite(statedCount) && statedCount === rowCount) {
    return statedCount === 1
      ? t('Confirm the gene list ({{count}} gene, as stated)', {
          count: statedCount
        })
      : t('Confirm the gene list ({{count}} genes, as stated)', {
          count: statedCount
        });
  }
  if (Number.isFinite(statedCount)) {
    const vars = { count: rowCount, stated: statedCount };
    return rowCount === 1
      ? t(
          'Confirm the gene list ({{count}} gene; the report states {{stated}})',
          vars
        )
      : t(
          'Confirm the gene list ({{count}} genes; the report states {{stated}})',
          vars
        );
  }
  if (rowCount > 0) {
    return rowCount === 1
      ? t('Confirm the gene list ({{count}} gene)', { count: rowCount })
      : t('Confirm the gene list ({{count}} genes)', { count: rowCount });
  }
  return t('Confirm the gene list');
}

export const CONFIRM_LABELS = Object.freeze({
  'variant-identity': 'Confirm the variant',
  'variant-classification': 'Confirm the classification',
  'overall-result': 'Confirm the overall result'
});

/**
 * "3 of 4 parts confirmed": the parts of the report, as every other line of the view and the save's
 * own refusal ("Confirm every part of the report") call them.
 */
export const groupsConfirmedText = (confirmed, total, t = english) =>
  total === 1
    ? t('{{confirmed}} of {{total}} part confirmed', { confirmed, total })
    : t('{{confirmed}} of {{total}} parts confirmed', { confirmed, total });

/** Why "complete" cannot be chosen for a list of fewer genes than the report states (decision 14). */
export const completeShortText = (statedCount, rowCount, t = english) => {
  // The choice it names is the gene list's own option, in the words the option is shown in.
  const vars = {
    stated: statedCount,
    count: rowCount,
    option: t('Only some of the genes are recorded')
  };
  if (statedCount === 1) {
    return rowCount === 1
      ? t(
          'Not available: the report states {{stated}} gene and {{count}} is listed. Enter the rest, or choose “{{option}}”.',
          vars
        )
      : t(
          'Not available: the report states {{stated}} gene and {{count}} are listed. Enter the rest, or choose “{{option}}”.',
          vars
        );
  }
  return rowCount === 1
    ? t(
        'Not available: the report states {{stated}} genes and {{count}} is listed. Enter the rest, or choose “{{option}}”.',
        vars
      )
    : t(
        'Not available: the report states {{stated}} genes and {{count}} are listed. Enter the rest, or choose “{{option}}”.',
        vars
      );
};
export const COMPLETE_NEEDS_A_GENE =
  'Not available: a complete list names at least one gene.';
export const PARTIAL_NEEDS_A_GENE = 'Not available: no gene is listed yet.';

// ── The group cards, in place on the Test (plan D3) ─────────────────────────────────────────────

/** "Confirmed at 14:05": a confirmation made in this review, by the reader's own clock. */
export const confirmedAtText = (time, t = english) =>
  time ? t('Confirmed at {{time}}', { time }) : t(CONFIRMED);
// Said beside a Confirm that waits, before the list of what it waits for.
export const BEFORE_CONFIRMING = 'Before confirming:';
// A value left empty for the clinician, and why ("Not filled in: the report does not state it"):
// `reasonText` in words, already translated.
export const notFilledInText = (reasonText, t = english) =>
  t('Not filled in: {{reason}}', { reason: reasonText });
// What the Test held where the application emptied a value the reader was not confident of, said
// beside the empty field so it can be chosen or typed again if the report says so.
export const heldBeforeText = (value, t = english) =>
  t('This Test held: {{value}}', { value });

// The low-confidence values of a group, as their fields are labelled where they are entered.
export const LOW_FIELD_LABELS = Object.freeze({
  variantC: 'c.',
  variantP: 'p.',
  variantG: 'g.',
  transcript: 'Transcript',
  genomeBuild: 'Genome build',
  zygosity: 'Zygosity',
  variantInterpretation: 'Variant Interpretation',
  'labClassification.classificationDate': 'Classification date',
  testResult: 'Test Result'
});

/** "Genes tested (5)": how many genes this Test will record as tested. */
export const genesTestedCountText = (count, t = english) =>
  t('Genes tested ({{count}})', { count });
/** "Coverage: 5 complete, 0 incomplete, 0 not stated." */
export const coverageCountsText = (
  { complete, incomplete, notStated },
  t = english
) =>
  t(
    'Coverage: {{complete}} complete, {{incomplete}} incomplete, {{notStated}} not stated.',
    { complete, incomplete, notStated }
  );

// A finding of the report that no variant on the Test carries: its parts are still confirmed, and
// nothing of it is saved (the save records each as none captured).
export const NOT_ON_TEST_HEADING = 'Read from the report, not on this Test';
export const NOT_ON_TEST_KEPT =
  'This Test keeps its own variant, so nothing of the report’s is saved.';
export const NOT_ON_TEST_REMOVED =
  'Removed from this Test, so nothing of it is saved.';
export const NOT_ON_TEST_CONFIRM =
  'Confirm each part to record that it was checked.';

// ── The footer (plan D4) ────────────────────────────────────────────────────────────────────────

/** "Not read from this report (2)". */
export const notReadHeadingText = (count, t = english) =>
  t('Not read from this report ({{count}})', { count });
/** "Show all 5", or "Show fewer". */
export const showMoreText = (open, count, t = english) =>
  open ? t('Show fewer') : t('Show all {{count}}', { count });
export const SAVE_RECORDS_REPORT =
  'Saving records the lab report applied to this Test.';
export const BEFORE_SAVING = 'Before saving:';
